import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { employeesApi, authApi } from '../services/api';

interface Employee {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'manager' | 'staff';
  created_at: string;
}

const Employees: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await employeesApi.getAll();
      setEmployees(response.data.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role: string) => {
    const colors: { [key: string]: 'success' | 'warning' | 'info' | 'default' } = {
      admin: 'default',
      manager: 'warning',
      staff: 'info',
    };
    return colors[role] || 'default';
  };

  const handleEdit = (employee: Employee) => {
    setEditingEmployee(employee);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEmployee(null);
  };

  const handleSave = async () => {
    try {
      const formData = new FormData(document.getElementById('employee-form') as HTMLFormElement);
      const email = formData.get('email') as string;
      const password = formData.get('password') as string;
      const employeeData = {
        first_name: formData.get('first_name'),
        last_name: formData.get('last_name'),
        role: formData.get('role'),
      };

      if (editingEmployee) {
        await employeesApi.update(editingEmployee.id, employeeData);
      } else {
        // Create new employee via registration
        if (!email || !password) {
          setError('Email and password are required for new employees');
          return;
        }
        await authApi.register({
          email,
          password,
          firstName: employeeData.first_name as string,
          lastName: employeeData.last_name as string,
          role: employeeData.role as string,
        });
      }
      handleCloseDialog();
      fetchEmployees();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save employee');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const stats = {
    total: employees.length,
    admin: employees.filter(e => e.role === 'admin').length,
    manager: employees.filter(e => e.role === 'manager').length,
    staff: employees.filter(e => e.role === 'staff').length,
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Employees Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Employee
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Employees</Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Admins</Typography>
              <Typography variant="h4" color="error.main">{stats.admin}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Managers</Typography>
              <Typography variant="h4" color="warning.main">{stats.manager}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Staff</Typography>
              <Typography variant="h4" color="info.main">{stats.staff}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="textSecondary" sx={{ py: 3 }}>
                    No employees found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              employees.map((employee) => (
                <TableRow key={employee.id} hover>
                  <TableCell>
                    {employee.first_name} {employee.last_name}
                  </TableCell>
                  <TableCell>{employee.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={employee.role}
                      color={getRoleColor(employee.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(employee.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(employee)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this employee?')) {
                          try {
                            await employeesApi.delete(employee.id);
                            fetchEmployees();
                          } catch (err: any) {
                            setError(err.response?.data?.message || 'Failed to delete employee');
                          }
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
        <form id="employee-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <DialogContent>
            {!editingEmployee && (
              <>
                <TextField
                  fullWidth
                  name="email"
                  label="Email"
                  type="email"
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  margin="normal"
                  required
                  helperText="Minimum 6 characters"
                />
              </>
            )}
            <TextField
              fullWidth
              name="first_name"
              label="First Name"
              margin="normal"
              defaultValue={editingEmployee?.first_name || ''}
              required
            />
            <TextField
              fullWidth
              name="last_name"
              label="Last Name"
              margin="normal"
              defaultValue={editingEmployee?.last_name || ''}
              required
            />
            <TextField
              fullWidth
              name="role"
              select
              label="Role"
              margin="normal"
              defaultValue={editingEmployee?.role || 'staff'}
              required
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="staff">Staff</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingEmployee ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Employees;

